import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { isGlobalAdmin } from '../services/licenses';
import Loading from '../components/Loading';
import UserManagement from '../components/UserManagement';
import PlayerAnonymization from '../components/PlayerAnonymization';
import SoftDeletedPlayerCleanup from '../components/SoftDeletedPlayerCleanup';
import './GlobalAdmin.css';

interface AdminStats {
  totalUsers: number;
  totalClubs: number;
  totalGames: number;
  totalFrames: number;
  totalBreaks: number;
  activeUsers: number;
  clubLicenseUsers: number;
  globalAdmins: number;
}

interface RecentUser {
  id: string;
  email: string;
  created_at: string;
  license_type: string;
  global_admin: boolean;
}

interface RecentClub {
  id: number;
  name: string;
  created_at: string;
  member_count: number;
  owner_email: string;
}

const GlobalAdmin: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [recentClubs, setRecentClubs] = useState<RecentClub[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'anonymization' | 'cleanup'>('overview');

  useEffect(() => {
    checkAccessAndLoadData();
  }, []);

  const checkAccessAndLoadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user has global admin access
      const isAdmin = await isGlobalAdmin();
      if (!isAdmin) {
        setError(t('globalAdmin.accessDenied'));
        setLoading(false);
        return;
      }

      setHasAccess(true);
      await loadAdminData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadAdminData = async () => {
    try {
      // Load statistics
      const [
        usersCount,
        clubsCount,
        gamesCount,
        framesCount,
        breaksCount,
        activeUsersData,
        clubLicenseData,
        globalAdminsData,
        recentUsersData,
        recentClubsData
      ] = await Promise.all([
        supabase.from('players').select('*', { count: 'exact', head: true }),
        supabase.from('clubs').select('*', { count: 'exact', head: true }),
        supabase.from('games').select('*', { count: 'exact', head: true }),
        supabase.from('frames').select('*', { count: 'exact', head: true }),
        supabase.from('breaks').select('*', { count: 'exact', head: true }),
        supabase.from('players').select('playerUUID').not('playerUUID', 'is', null),
        supabase.from('user_licenses').select('*').eq('license_type', 'club'),
        supabase.from('user_licenses').select('*').eq('global_admin', true),
        supabase
          .from('players')
          .select(`
            id,
            playerUUID,
            "preName",
            name,
            email,
            created_at
          `)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('clubs')
          .select(`
            id,
            name,
            created_at,
            created_by
          `)
          .order('created_at', { ascending: false })
          .limit(10)
      ]);

      setStats({
        totalUsers: usersCount.count || 0,
        totalClubs: clubsCount.count || 0,
        totalGames: gamesCount.count || 0,
        totalFrames: framesCount.count || 0,
        totalBreaks: breaksCount.count || 0,
        activeUsers: activeUsersData.data?.length || 0,
        clubLicenseUsers: clubLicenseData.data?.length || 0,
        globalAdmins: globalAdminsData.data?.length || 0,
      });

      // Process recent users
      const processedUsers: RecentUser[] = (recentUsersData.data || []).map((user: any) => ({
        id: user.playerUUID,
        email: user.email || 'N/A',
        created_at: user.created_at,
        license_type: 'basic', // Default for now, would need separate query for license info
        global_admin: false, // Default for now, would need separate query for admin status
      }));
      setRecentUsers(processedUsers);

      // Process recent clubs
      const processedClubs: RecentClub[] = (recentClubsData.data || []).map((club: any) => ({
        id: club.id,
        name: club.name,
        created_at: club.created_at,
        member_count: 0, // Would need additional query for member count
        owner_email: club.created_by || 'N/A',
      }));
      setRecentClubs(processedClubs);

    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load admin data');
    }
  };

  if (loading) return <Loading />;

  if (!hasAccess) {
    return (
      <div className="global-admin-container">
        <div className="access-denied">
          <h1>🔒 {t('globalAdmin.accessDenied')}</h1>
          <p>{t('globalAdmin.accessDeniedMessage')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="global-admin-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={checkAccessAndLoadData} className="btn btn-secondary">
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="global-admin-container">
      <div className="admin-header">
        <h1>🌐 {t('globalAdmin.title')}</h1>
        <p className="admin-subtitle">{t('globalAdmin.subtitle')}</p>
      </div>

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 User Management
        </button>
        <button
          className={`tab-button ${activeTab === 'anonymization' ? 'active' : ''}`}
          onClick={() => setActiveTab('anonymization')}
        >
          ⚠️ Player Anonymization
        </button>
        <button
          className={`tab-button ${activeTab === 'cleanup' ? 'active' : ''}`}
          onClick={() => setActiveTab('cleanup')}
        >
          🗑️ Cleanup Deleted Players
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="tab-content">

      {/* Statistics Overview */}
      <div className="stats-section">
        <h2>{t('globalAdmin.statistics')}</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-number">{stats?.totalUsers || 0}</div>
              <div className="stat-label">{t('globalAdmin.totalUsers')}</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🏢</div>
            <div className="stat-content">
              <div className="stat-number">{stats?.totalClubs || 0}</div>
              <div className="stat-label">{t('globalAdmin.totalClubs')}</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🎱</div>
            <div className="stat-content">
              <div className="stat-number">{stats?.totalGames || 0}</div>
              <div className="stat-label">{t('globalAdmin.totalGames')}</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-number">{stats?.totalFrames || 0}</div>
              <div className="stat-label">{t('globalAdmin.totalFrames')}</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <div className="stat-number">{stats?.totalBreaks || 0}</div>
              <div className="stat-label">{t('globalAdmin.totalBreaks')}</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🔑</div>
            <div className="stat-content">
              <div className="stat-number">{stats?.clubLicenseUsers || 0}</div>
              <div className="stat-label">{t('globalAdmin.clubLicenses')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="activity-section">
        <div className="activity-grid">
          {/* Recent Users */}
          <div className="activity-card">
            <h3>👤 {t('globalAdmin.recentUsers')}</h3>
            <div className="activity-list">
              {recentUsers.length === 0 ? (
                <p className="no-data">{t('globalAdmin.noRecentUsers')}</p>
              ) : (
                recentUsers.map((user) => (
                  <div key={user.id} className="activity-item">
                    <div className="item-info">
                      <div className="item-title">{user.email}</div>
                      <div className="item-meta">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="item-badges">
                      <span className={`license-badge ${user.license_type}`}>
                        {user.license_type}
                      </span>
                      {user.global_admin && (
                        <span className="admin-badge">ADMIN</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Clubs */}
          <div className="activity-card">
            <h3>🏢 {t('globalAdmin.recentClubs')}</h3>
            <div className="activity-list">
              {recentClubs.length === 0 ? (
                <p className="no-data">{t('globalAdmin.noRecentClubs')}</p>
              ) : (
                recentClubs.map((club) => (
                  <div key={club.id} className="activity-item">
                    <div className="item-info">
                      <div className="item-title">{club.name}</div>
                      <div className="item-meta">
                        {new Date(club.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="item-badges">
                      <span className="member-count">
                        {club.member_count} members
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="actions-section">
        <h2>{t('globalAdmin.quickActions')}</h2>
        <div className="actions-grid">
          <button className="action-btn" onClick={() => alert('Coming soon!')}>
            <div className="action-icon">👥</div>
            <div className="action-text">{t('globalAdmin.manageUsers')}</div>
          </button>
          
          <button className="action-btn" onClick={() => alert('Coming soon!')}>
            <div className="action-icon">🏢</div>
            <div className="action-text">{t('globalAdmin.manageClubs')}</div>
          </button>
          
          <button className="action-btn" onClick={() => alert('Coming soon!')}>
            <div className="action-icon">🔑</div>
            <div className="action-text">{t('globalAdmin.manageLicenses')}</div>
          </button>
          
          <button className="action-btn" onClick={() => alert('Coming soon!')}>
            <div className="action-icon">🏆</div>
            <div className="action-text">{t('globalAdmin.manageEvents')}</div>
          </button>
        </div>
      </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="tab-content">
          <UserManagement />
        </div>
      )}

      {activeTab === 'anonymization' && (
        <div className="tab-content">
          <PlayerAnonymization onAnonymizationComplete={() => checkAccessAndLoadData()} />
        </div>
      )}

      {activeTab === 'cleanup' && (
        <div className="tab-content">
          <SoftDeletedPlayerCleanup onCleanupComplete={() => checkAccessAndLoadData()} />
        </div>
      )}
    </div>
  );
};

export default GlobalAdmin;
